/**
 * German Grammar Helper
 *
 * Comprehensive utilities for German grammar, pronouns, articles, and declensions.
 * Handles gender-based variations for personalized content generation.
 *
 * Supported genders: 'male' (m), 'female' (w), 'other' (default)
 */

export type Gender = 'male' | 'female' | 'other';

/**
 * German pronouns in nominative case (subject)
 * Used for: "er/sie/es ist..."
 */
export const NOMINATIVE_PRONOUNS: Record<Gender, string> = {
  male: 'er',
  female: 'sie',
  other: 'er/sie'
};

/**
 * German pronouns in accusative case (direct object)
 * Used for: "Ich sehe ihn/sie/es"
 */
export const ACCUSATIVE_PRONOUNS: Record<Gender, string> = {
  male: 'ihn',
  female: 'sie',
  other: 'ihn/sie'
};

/**
 * German pronouns in dative case (indirect object)
 * Used for: "Ich gebe ihm/ihr/ihm etwas"
 */
export const DATIVE_PRONOUNS: Record<Gender, string> = {
  male: 'ihm',
  female: 'ihr',
  other: 'ihm/ihr'
};

/**
 * German possessive pronouns (nominative)
 * Used for: "sein/ihr/sein Buch"
 */
export const POSSESSIVE_NOMINATIVE: Record<Gender, string> = {
  male: 'sein',
  female: 'ihr',
  other: 'sein/ihr'
};

/**
 * German possessive pronouns (accusative)
 * Used for: "Ich sehe seinen/ihren/seinen Buch"
 */
export const POSSESSIVE_ACCUSATIVE: Record<Gender, string> = {
  male: 'seinen',
  female: 'ihren',
  other: 'seinen/ihren'
};

/**
 * German possessive pronouns (dative)
 * Used for: "Ich gebe seinem/ihrem/seinem Buch"
 */
export const POSSESSIVE_DATIVE: Record<Gender, string> = {
  male: 'seinem',
  female: 'ihrem',
  other: 'seinem/ihrem'
};

/**
 * German possessive pronouns (genitive)
 * Used for: "Das Buch seines/ihres/seines Kindes"
 */
export const POSSESSIVE_GENITIVE: Record<Gender, string> = {
  male: 'seines',
  female: 'ihres',
  other: 'seines/ihres'
};

/**
 * German reflexive pronouns
 * Used for: "Er/Sie interessiert sich..."
 */
export const REFLEXIVE_PRONOUNS: Record<Gender, string> = {
  male: 'sich',
  female: 'sich',
  other: 'sich'
};

/**
 * Get nominative pronoun (subject)
 * @example getPronoun('male') => 'er'
 */
export function getPronoun(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return NOMINATIVE_PRONOUNS[g];
}

/**
 * Get accusative pronoun (direct object)
 * @example getAccusativePronoun('female') => 'sie'
 */
export function getAccusativePronoun(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return ACCUSATIVE_PRONOUNS[g];
}

/**
 * Get dative pronoun (indirect object)
 * @example getDativePronoun('male') => 'ihm'
 */
export function getDativePronoun(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return DATIVE_PRONOUNS[g];
}

/**
 * Get possessive pronoun (nominative)
 * @example getPossessivePronoun('female') => 'ihr'
 */
export function getPossessivePronoun(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return POSSESSIVE_NOMINATIVE[g];
}

/**
 * Get possessive pronoun (accusative)
 * @example getPossessiveAccusative('male') => 'seinen'
 */
export function getPossessiveAccusative(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return POSSESSIVE_ACCUSATIVE[g];
}

/**
 * Get possessive pronoun (dative)
 * @example getPossessiveDative('female') => 'ihrem'
 */
export function getPossessiveDative(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return POSSESSIVE_DATIVE[g];
}

/**
 * Get possessive pronoun (genitive)
 * @example getPossessiveGenitive('male') => 'seines'
 */
export function getPossessiveGenitive(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return POSSESSIVE_GENITIVE[g];
}

/**
 * Get reflexive pronoun
 * @example getReflexivePronoun('male') => 'sich'
 */
export function getReflexivePronoun(gender?: Gender | string): string {
  const g = normalizeGender(gender);
  return REFLEXIVE_PRONOUNS[g];
}

/**
 * Normalize gender input to standard Gender type
 * Handles common variations: 'male', 'm', 'Male', 'MALE', etc.
 */
export function normalizeGender(gender?: Gender | string): Gender {
  if (!gender) return 'other';

  const normalized = gender.toLowerCase().trim();

  if (normalized === 'male' || normalized === 'm') return 'male';
  if (normalized === 'female' || normalized === 'w' || normalized === 'f') return 'female';

  return 'other';
}

/**
 * Get all pronouns for a gender (useful for templates)
 */
export function getAllPronouns(gender?: Gender | string) {
  const g = normalizeGender(gender);

  return {
    nominative: NOMINATIVE_PRONOUNS[g],
    accusative: ACCUSATIVE_PRONOUNS[g],
    dative: DATIVE_PRONOUNS[g],
    possessiveNominative: POSSESSIVE_NOMINATIVE[g],
    possessiveAccusative: POSSESSIVE_ACCUSATIVE[g],
    possessiveDative: POSSESSIVE_DATIVE[g],
    possessiveGenitive: POSSESSIVE_GENITIVE[g],
    reflexive: REFLEXIVE_PRONOUNS[g]
  };
}

/**
 * Format a sentence with gender-aware pronouns
 * @example formatWithPronouns('${pronoun} ist ein Lerner', 'male') => 'er ist ein Lerner'
 */
export function formatWithPronouns(
  template: string,
  gender?: Gender | string,
  pronouns?: Partial<Record<keyof ReturnType<typeof getAllPronouns>, string>>
): string {
  const g = normalizeGender(gender);
  const allPronouns = getAllPronouns(g);
  const customPronouns = pronouns || {};

  let result = template;

  // Replace placeholders with pronouns
  result = result.replace(/\$\{pronoun\}/g, customPronouns.nominative || allPronouns.nominative);
  result = result.replace(/\$\{accusativePronoun\}/g, customPronouns.accusative || allPronouns.accusative);
  result = result.replace(/\$\{dativePronoun\}/g, customPronouns.dative || allPronouns.dative);
  result = result.replace(/\$\{possessivePronoun\}/g, customPronouns.possessiveNominative || allPronouns.possessiveNominative);
  result = result.replace(/\$\{possessiveAccusative\}/g, customPronouns.possessiveAccusative || allPronouns.possessiveAccusative);
  result = result.replace(/\$\{possessiveDative\}/g, customPronouns.possessiveDative || allPronouns.possessiveDative);
  result = result.replace(/\$\{possessiveGenitive\}/g, customPronouns.possessiveGenitive || allPronouns.possessiveGenitive);
  result = result.replace(/\$\{reflexivePronoun\}/g, customPronouns.reflexive || allPronouns.reflexive);

  return result;
}

